import { LocalDate } from "@js-joda/core";
import { keepPreviousData, type UseQueryResult, useQueries } from "@tanstack/react-query";
import useApi from "../../../../../shared/hooks/useApi";
import {
  getEntityCalendarVisibleMonthDates,
  splitEntityCalendarRange,
  uniqueEntityCalendarItemsBy,
} from "../../../../entityProfileModal/sections/calendar/entityProfileCalendar.utils";
import type { CaregiverProfileCaregiver } from "../../../caregiverProfileModal.types";
import type { CaregiverCalendarResponse } from "./caregiverCalendar.types";

/**
 * Every calendar query of the caregiver starts with this key, so a refresh can
 * refetch all of them (calendar and list view, every range) at once.
 */
export function getCaregiverCalendarQueryKeyPrefix(caregiverId: CaregiverProfileCaregiver["id"]) {
  return ["caregiverProfileModal", caregiverId, "calendar"] as const;
}

export function useCaregiverProfileCalendarMonth(
  caregiverId: CaregiverProfileCaregiver["id"],
  monthDate: LocalDate,
  enabled = true
) {
  const visibleDates = getEntityCalendarVisibleMonthDates(monthDate);
  const calendarQuery = useCaregiverProfileCalendarRange(
    caregiverId,
    visibleDates.queryFrom,
    visibleDates.queryTo,
    enabled
  );

  return {
    calendarQuery,
    visibleDates,
  };
}

/**
 * The caregiver calendar for a range. Ranges longer than the endpoint accepts
 * (only the list view can pick one) are fetched in parts and merged.
 */
export function useCaregiverProfileCalendarRange(
  caregiverId: CaregiverProfileCaregiver["id"],
  from: LocalDate,
  to: LocalDate,
  enabled = true
) {
  const { api } = useApi();

  return useQueries({
    queries: splitEntityCalendarRange(from, to).map((range) => ({
      queryKey: [
        ...getCaregiverCalendarQueryKeyPrefix(caregiverId),
        range.from.toString(),
        range.to.toString(),
      ],
      queryFn: () =>
        api.get("./dashboard/caregivers/:caregiverId/calendar", {
          path: { caregiverId },
          query: {
            from: range.from,
            to: range.to,
          },
        }),
      enabled,
      placeholderData: keepPreviousData<CaregiverCalendarResponse>,
      // Refreshed explicitly after every change (`medflyt:refresh_visits`).
      // Refetching on focus reloaded every mounted calendar each time the
      // user came back to the tab.
      refetchOnWindowFocus: false,
    })),
    combine: combineCaregiverCalendarRanges,
  });
}

function combineCaregiverCalendarRanges(results: UseQueryResult<CaregiverCalendarResponse>[]) {
  const responses = results.flatMap((result) => (result.data === undefined ? [] : [result.data]));

  return {
    data:
      responses.length === results.length && responses.length > 0
        ? mergeCaregiverCalendarResponses(responses)
        : undefined,
    isError: results.some((result) => result.isError),
    isFetched: results.every((result) => result.isFetched),
    isLoading: results.some((result) => result.isLoading),
    isPlaceholderData: results.some((result) => result.isPlaceholderData),
    isSuccess: results.every((result) => result.isSuccess),
    refetch: () => Promise.all(results.map((result) => result.refetch())),
  };
}

function mergeCaregiverCalendarResponses(
  responses: CaregiverCalendarResponse[]
): CaregiverCalendarResponse {
  if (responses.length === 1) {
    return responses[0];
  }

  return {
    visitInstances: uniqueEntityCalendarItemsBy(
      responses.flatMap((response) => response.visitInstances),
      (visitInstance) => visitInstance.visit.id
    ),
    ptoInstances: {
      caregiverPtos: uniqueEntityCalendarItemsBy(
        responses.flatMap((response) => response.ptoInstances.caregiverPtos),
        (pto) => pto.id
      ),
    },
    notes: uniqueEntityCalendarItemsBy(
      responses.flatMap((response) => response.notes),
      (note) => note.id
    ),
    absences: uniqueEntityCalendarItemsBy(
      responses.flatMap((response) => response.absences),
      (absence) => absence.id
    ),
  };
}
